import { Controller } from "@hotwired/stimulus"

// Character picker modal for deck builder.
// Handles: open/close, filter (element/rating/job), search, selection.
//
// Each character card in the modal has data-* attributes the controller
// reads to decide whether to show or hide it based on active filters.
export default class extends Controller {
  static targets = ["modal", "input", "card",
                    "pickerCombatantSet", "placeholder",
                    "selectedCard"
                  ]

  connect() {
    this._onKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
  }

  onKeydown(e) {
    if (e.key !== "Escape") return
    if (this.modalTarget.classList.contains("hidden")) return

    const searchEl = this.element.querySelector("[data-controller='character-search']")
    if (searchEl) {
      const searchController = this.application.getControllerForElementAndIdentifier(searchEl, "character-search")
      if (searchController?.enabled) return
    }

    this.close()
  }

  // ----- Open/close -----

  open() {
    this.modalTarget.classList.remove("hidden")
  }

  close() {
    this.modalTarget.classList.add("hidden")

    const searchEl = this.element.querySelector("[data-controller='character-search']")
    if (searchEl) {
      const searchController = this.application.getControllerForElementAndIdentifier(searchEl, "character-search")
      if (searchController && searchController.enabled) {
        searchController.reset()
      }
    }
  }

  closeIfBackdrop(event) {
    if (event.target === this.modalTarget) this.close()
  }

  // ----- Selection -----
  select(event) {
    const card = event.currentTarget
    const id = card.dataset.characterId

    this.inputTarget.value = id

    // Update the trigger area
    this.placeholderTarget.classList.add("hidden")
    this.selectedCardTargets.forEach(selectedCard => {
      if (selectedCard.dataset.characterId === id) {
        selectedCard.classList.remove("hidden")
      } else {
        selectedCard.classList.add("hidden")
      }
    })

    // Update the picker's combatant section
    this.pickerCombatantSetTargets.forEach(combatantSet => {
      if (combatantSet.dataset.characterId === id) {
        combatantSet.classList.remove("hidden")
      } else {
        combatantSet.classList.add("hidden")
      }
    })

    // Shrink trigger to just the face image
    this.element.querySelector(".character-picker-trigger").classList.add("character-picker-trigger--selected")

    // Notify other controllers (deck controller renders the cards area)
    this.dispatch("characterSelected", { detail: { characterId: id, characterJobs: card.dataset.characterJobs, characterElement: card.dataset.element } })

    this.close()
  }

  // ----- Filtering -----

  filterElement(event) {
    this.toggleFilter("element", event.currentTarget)
  }

  filterRating(event) {
    this.toggleFilter("rating", event.currentTarget)
  }

  filterJob(event) {
    this.toggleFilter("job", event.currentTarget)
  }

  toggleFilter(filterKey, button) {
    const value = button.dataset.filterValue
    const isActive = button.dataset.active === "true"

    // Find all buttons in this filter group and deactivate them
    const groupSelector = `[data-filter-group="${filterKey}"]`
    this.element.querySelectorAll(groupSelector).forEach(b => {
      b.dataset.active = "false"
      b.classList.add("brightness-50")
    })

    // Activate the clicked button (unless it was already active = toggle off)
    if (!isActive) {
      button.dataset.active = "true"
      button.classList.remove("brightness-50")
    }

    this.applyFilters()
  }

  applyFilters() {
    const activeElement = this.activeFilterValue("element")
    const activeRating = this.activeFilterValue("rating")
    const activeJob = this.activeFilterValue("job")

    this.cardTargets.forEach(card => {
      const matchesElement = !activeElement || card.dataset.element.toLowerCase() === activeElement.toLowerCase()
      const matchesRating = !activeRating || card.dataset.rating === activeRating
      const matchesJob = !activeJob || card.dataset.job.toLowerCase() === activeJob.toLowerCase()

      const visible = matchesElement && matchesRating && matchesJob
      card.classList.toggle("hidden", !visible)
    })
  }

  activeFilterValue(filterKey) {
    const button = this.element.querySelector(`[data-filter-group="${filterKey}"][data-active="true"]`)
    return button ? button.dataset.filterValue : null
  }
}
